<div class="visit-form vf">
  <div class="vf__stripe"></div>

  <header class="vf__head">
    <span class="vf__head-icon"><mat-icon fontSet="material-icons-outlined">{{ editing ? "edit_calendar" : "event_available" }}</mat-icon></span>
    <div class="vf__head-text">
      <h2 class="vf__title">{{ editing ? "Edit visit" : "New visit" }}</h2>
      <p class="vf__subtitle">
        {{ editing ? "Attendees get an updated invitation when you save." : "Attendees get an email invitation with a calendar file (.ics)." }}
      </p>
    </div>
    <button type="button" class="vf__close" aria-label="Close" [disabled]="saving" (click)="cancel()">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <form [formGroup]="form" (ngSubmit)="save()" class="vf__form" novalidate>
    <div class="vf__body">
      <!-- Title -->
      <div class="vf__field">
        <label class="vf__label" for="vf-title">Title<span class="vf__req">*</span></label>
        <input
          id="vf-title"
          class="vf__input"
          type="text"
          formControlName="title"
          maxlength="200"
          placeholder="e.g. Quarterly product review"
          [class.vf__input--invalid]="fieldInvalid('title')"
        />
        <p class="vf__msg" *ngIf="fieldInvalid('title')">{{ fieldError("title") }}</p>
      </div>

      <!-- Visit type -->
      <div class="vf__field">
        <span class="vf__label" id="vf-type-label">Visit type</span>
        <div class="vf__types" role="radiogroup" aria-labelledby="vf-type-label">
          <label class="vf__type" [class.vf__type--on]="!isVideo">
            <input type="radio" formControlName="visitType" value="IN_PERSON" />
            <span class="vf__type-icon"><mat-icon fontSet="material-icons-outlined">place</mat-icon></span>
            <span class="vf__type-text"><b>In-person</b><small>At a clinic or office</small></span>
            <mat-icon class="vf__type-check">{{ !isVideo ? "check_circle" : "radio_button_unchecked" }}</mat-icon>
          </label>
          <label class="vf__type vf__type--video" [class.vf__type--on]="isVideo">
            <input type="radio" formControlName="visitType" value="VIDEO" />
            <span class="vf__type-icon"><mat-icon fontSet="material-icons-outlined">videocam</mat-icon></span>
            <span class="vf__type-text"><b>Video</b><small>Secure SCRAII video room</small></span>
            <mat-icon class="vf__type-check">{{ isVideo ? "check_circle" : "radio_button_unchecked" }}</mat-icon>
          </label>
        </div>
      </div>

      <!-- When -->
      <div class="vf__field">
        <span class="vf__label">When</span>
        <div class="vf__when">
          <div class="vf__sub-field vf__sub-field--date">
            <label class="vf__mini" for="vf-date">Date</label>
            <input id="vf-date" class="vf__input" type="date" formControlName="date" [class.vf__input--invalid]="fieldInvalid('date')" />
          </div>
          <div class="vf__sub-field">
            <label class="vf__mini" for="vf-start">Start</label>
            <input
              id="vf-start"
              class="vf__input"
              type="time"
              formControlName="startTime"
              [class.vf__input--invalid]="fieldInvalid('startTime') || !!timeRangeError"
            />
          </div>
          <div class="vf__sub-field">
            <label class="vf__mini" for="vf-end">End</label>
            <input
              id="vf-end"
              class="vf__input"
              type="time"
              formControlName="endTime"
              [class.vf__input--invalid]="fieldInvalid('endTime') || !!timeRangeError"
            />
          </div>
          <div class="vf__sub-field vf__sub-field--zone">
            <label class="vf__mini" for="vf-zone">Time zone</label>
            <select id="vf-zone" class="vf__input vf__select" formControlName="timeZone">
              <option *ngFor="let z of timeZones" [value]="z.id">{{ z.label }}</option>
            </select>
          </div>
        </div>
        <p class="vf__msg" *ngIf="whenError; else durationHint">{{ whenError }}</p>
        <ng-template #durationHint>
          <p class="vf__hint" *ngIf="durationLabel">
            <mat-icon fontSet="material-icons-outlined">schedule</mat-icon>{{ durationLabel }}
          </p>
        </ng-template>
      </div>

      <!-- Where -->
      <div class="vf__field" *ngIf="!isVideo">
        <label class="vf__label" for="vf-location">Location</label>
        <div class="vf__affix">
          <mat-icon fontSet="material-icons-outlined">place</mat-icon>
          <input
            id="vf-location"
            class="vf__input"
            type="text"
            formControlName="location"
            maxlength="500"
            placeholder="Clinic or office address"
          />
        </div>
      </div>
      <div class="vf__note" *ngIf="isVideo">
        <mat-icon fontSet="material-icons-outlined">videocam</mat-icon>
        <span>A secure SCRAII video room is created when you save. Each attendee gets a personal join link and meeting code by email.</span>
      </div>

      <!-- Notes -->
      <div class="vf__field">
        <div class="vf__label-row">
          <label class="vf__label" for="vf-notes">Notes</label>
          <span class="vf__counter">{{ notesLength }} / 2000</span>
        </div>
        <textarea
          id="vf-notes"
          class="vf__input vf__textarea"
          rows="3"
          formControlName="notes"
          maxlength="2000"
          placeholder="Agenda or details to include in the invitation"
        ></textarea>
      </div>

      <!-- Attendees: an email-style field. Pick people from the user directory, or type an address and press Enter. -->
      <div class="vf__field vf__people">
        <div class="vf__label-row">
          <label class="vf__label" for="vf-people">Attendees<b class="vf__count">{{ attendees.length }}</b></label>
        </div>

        <!-- The suggestions open under this whole box (matAutocompleteOrigin) and take its width. -->
        <div
          class="vf__chips"
          matAutocompleteOrigin
          #peopleOrigin="matAutocompleteOrigin"
          [class.vf__chips--invalid]="!!attendeesError"
          [class.vf__chips--disabled]="form.disabled"
          (click)="focusPeople()"
        >
          <span
            class="vf__chip"
            *ngFor="let group of attendeeGroups; let i = index"
            [class.vf__chip--invalid]="group.invalid"
            [title]="group.value.email"
          >
            <span class="vf__chip-avatar" aria-hidden="true">{{ chipInitials(group.value) }}</span>
            <span class="vf__chip-text">{{ group.value.displayName || group.value.email }}</span>
            <button
              type="button"
              class="vf__chip-remove"
              [attr.aria-label]="'Remove ' + (group.value.displayName || group.value.email)"
              [disabled]="form.disabled"
              (click)="$event.stopPropagation(); removeAttendee(i)"
            >
              <mat-icon>close</mat-icon>
            </button>
          </span>
          <input
            #peopleInput
            id="vf-people"
            class="vf__chip-input"
            type="text"
            autocomplete="off"
            [placeholder]="attendees.length ? 'Add another person or email' : 'Search people or type an email'"
            [value]="query"
            [disabled]="form.disabled || !canAddAttendee"
            [matAutocomplete]="peopleAuto"
            [matAutocompleteConnectedTo]="peopleOrigin"
            (input)="onQuery($any($event.target).value)"
            (keydown)="onPeopleKey($event)"
          />
        </div>

        <mat-autocomplete #peopleAuto="matAutocomplete" class="vf-people-panel" [displayWith]="noText" (optionSelected)="pickPerson($event)">
          <mat-option *ngFor="let p of suggestions" [value]="p">
            <span class="vf__opt">
              <span class="vf__opt-avatar" aria-hidden="true">{{ chipInitials({ displayName: p.name, email: p.email }) }}</span>
              <span class="vf__opt-text">
                <span class="vf__opt-name">{{ p.name }}</span>
                <span class="vf__opt-email">{{ p.email }}</span>
              </span>
            </span>
          </mat-option>
          <mat-option *ngIf="query.trim() && !people" disabled>Loading people…</mat-option>
        </mat-autocomplete>

        <p class="vf__msg" *ngIf="attendeesError; else peopleHintTpl">{{ attendeesError }}</p>
        <ng-template #peopleHintTpl>
          <p class="vf__hint">{{ peopleHint }}</p>
        </ng-template>
      </div>

      <div class="vf__banner vf__banner--error" *ngIf="errorMessage" role="alert">
        <mat-icon fontSet="material-icons-outlined">error</mat-icon><span>{{ errorMessage }}</span>
      </div>
      <div class="vf__banner vf__banner--warn" *ngIf="resultMessage" role="status">
        <mat-icon fontSet="material-icons-outlined">mark_email_unread</mat-icon><span>{{ resultMessage }}</span>
      </div>
    </div>

    <footer class="vf__foot">
      <ng-container *ngIf="!resultMessage; else doneTpl">
        <button type="button" class="vf__btn vf__btn--secondary" (click)="cancel()" [disabled]="saving">Cancel</button>
        <button type="submit" class="vf__btn vf__btn--primary" [disabled]="saving">
          <mat-icon>{{ saving ? "hourglass_top" : "send" }}</mat-icon>
          {{ saving ? "Saving…" : editing ? "Save and send update" : "Save and send invitations" }}
        </button>
      </ng-container>
      <ng-template #doneTpl>
        <button type="button" class="vf__btn vf__btn--primary" (click)="done()">Done</button>
      </ng-template>
    </footer>
  </form>
</div>
